import type {GuidedApiCallParams, GuidedItemsV2, GuidedItemV2} from '../models/models';
import {makeLogger} from '../../../logger/logger';
import {
  type AmbigousSuggestion,
  type CoseContextLocale,
  SuggestionBase,
  type SuggestionsResultV2ResponseDto
} from '../models/cose';
import {
  GuidedGenericQuickLink, GuidedHistoryLinkDetails, GuidedProductQuickLink,
  GuidedQuickLinkDetails,
  type GuidedV2TagStatus,
  QuickLinkBadges
} from '../models/quick-link';
import {quickLinkProducts} from '../subcomponents/quick-link-products/quick-links-products';
import type {GuidedV2SubcomponentUiProvider} from '../models/ui';
import {quickLinkVideoTutorials} from '../subcomponents/quick-link-video-tutorials/quick-link-video-tutorials';
import {quickLinkDownloadDocuments} from '../subcomponents/quick-link-download-documents/quick-link-download-documents';
import {getStatusTag, makeUrlForQuery, makeUrlForQueryBatteryReplacement} from '../utils';
import type {CoseGuidedV2Context} from '../../../models/cose-api/request/query-body-request.model';


const {
  info,
  // debug,
  warn,
  err
} = makeLogger({
  pathToCurrentFile: 'components/guided2/middlewares/mapCoseToGuidedV2.ts',
});





// @ts-ignore
// noinspection OverlyComplexFunctionJS
const mapCoseItem: <
  T extends SuggestionBase = AmbigousSuggestion,
  R extends GuidedQuickLinkDetails = GuidedGenericQuickLink,
> (groupDetails: {
  groupId: string,
  searcher: string,
  context: CoseGuidedV2Context,
}, item: T) => null | GuidedItemV2<R> = (groupDetails, item) => {
    try {
      const {
        groupId,
        searcher,
        context,
      } = groupDetails;

      const ia = item as AmbigousSuggestion;

      if (searcher.endsWith('/history')) {

        const result: GuidedItemV2<GuidedHistoryLinkDetails> = {
          score: 1,
          searcher: searcher,
          category: groupId,
          template: 'default',
          details: {
            quickLinkTitle: ia.term,
          }

        };
        return result;
      }

      if (searcher.startsWith('b2c/') && groupDetails?.groupId) {
        ia._routing = `B2c${groupDetails.groupId}`;
      }


      let quickLinkOpenUrl = '#';
      if (ia.visitUrl) {

        quickLinkOpenUrl = ia.visitUrl;

      } else if (ia?.userQuery) {

        let country = 'us';
        if (context.type == 'cose/context/locale' && 'country' in context) {
          country = context.country.toLowerCase();
        }

        let language = 'en';
        if ('language' in context) {
          language = context.language.toLowerCase();
        }
        quickLinkOpenUrl = makeUrlForQuery(country, language, ia.userQuery);

      } else {
        warn('quickLinkOpenUrl for item not found', {item});
      //TODO: logic not suitable for P&A
      }

      //DRUTY
      if (searcher.endsWith('rbc/selector/guided')) {
        quickLinkOpenUrl = makeUrlForQueryBatteryReplacement(ia.id);
      }

      const quickLinkTitle = ia?.titleHtml ?? ia?.titlePlain ?? ia?.userQuery ?? undefined;
      let quickLinkAttachment = null;

      if (quickLinkTitle == undefined) {
        warn('Title not found for item', {item});
        return null;
      }


      const badges: QuickLinkBadges = {attributes: [], tags: []};

      /*
source: https://jira.se.com/browse/SVS-30267


    Attr Category label = pierwszy tag, szary, bez name'a
    Attr Part number - "Part number"
    Attr EAN i E-nummer - wedug labelek z backendu


  tab documents and software
    routing: Documents, f3Cat lub f2Cat => Attr Category label
    routing: Software, f3Cat lub f2Cat => Attr Category label

  tab documents
    routing: Documents, f3Cat => Attr Category label
  tab software
    routing: Software, f2Cat => Attr Category label

    routing: Products, f2Cat => Attr Category label
    routing: Products, id => Attr Part number
    routing: Products, EAN i E-nummer => Atrybuty
    routing: FAQ. customerSupportJourney.value => Attr Category label
    routing: FAQ, video jeśli jest => Tag: Video Content
    routing: Support, f1Cat => Attr Category label

    routing: Company, f2Cat -> Attr Category label
    routing: Company, if f2Cat=="Video" then Tag: Video Content
    routing: Solutions, f2Cat -> Attr Category label

    routing Services, f1Cat => Attr Category Label
     */

      const getTemplate = () => {
          switch(ia._routing) {
          case 'Products':
          case 'B2cproducts':
            return quickLinkProducts() as unknown as GuidedV2SubcomponentUiProvider;
          case 'B2cvideotutorials':
            return quickLinkVideoTutorials() as unknown as GuidedV2SubcomponentUiProvider;
          case 'B2cdocuments':
            return quickLinkDownloadDocuments() as unknown as GuidedV2SubcomponentUiProvider;
          default:
            return 'default';
          }
      };

      //searchers se/guided groups: documents and documents-and-software
      if (ia._routing == 'Documents') {
        badges.attributes.push({
          attributeName: '',
          attributeValue: ia.f3Cat ?? ia.f2Cat,
          attributeType: 'category',
          importance: 'standard',
        });
      }
      if (ia._routing == 'Software') {
        badges.attributes.push({
          attributeName: '',
          attributeValue: ia.f3Cat ?? ia.f2Cat,
          attributeType: 'category',
          importance: 'standard',
        });
      }
      if (ia._routing == 'Products' && !ia.id) { // don't render category label if we have part number
        badges.attributes.push({
          attributeName: '',
          attributeValue: ia.f3Cat ?? ia.f2Cat,
          attributeType: 'category',
          importance: 'standard',
        });
      }
      if (ia._routing == 'Products' && ia.id) {
        badges.attributes.push({
          attributeName: 'Part number',
          attributeValue: ia.id,
          attributeType: 'category',
          importance: 'standard',
        });
      }

      if (ia._routing == 'FAQ' && ia.customerSupportJourney) {
        badges.attributes.push({
          attributeName: '',
          attributeValue: ia.customerSupportJourney.value,
          attributeType: 'category',
          importance: 'standard',
        });
      }
      if (ia._routing == 'FAQ' && ia.video) {
        if (ia.video.value == 'With videos') {
          badges.attributes.push({
            attributeName: ia.video.label,
            attributeValue: ia.video.value,
            attributeType: 'video',
            importance: 'emphasized',
          });
        }
      }
      if (ia._routing == 'Support') {
        const v = ia.f3Cat ?? ia.f2Cat ?? ia.f1Cat;
        if (v != 'Support') { //that would be redundant
          badges.attributes.push({
            attributeName: '',
            attributeValue: v,
            attributeType: 'category',
            importance: 'standard',
          });
        }
      }
      if (ia._routing == 'Company') {
        badges.attributes.push({
          attributeName: '',
          attributeValue: ia.f3Cat ?? ia.f2Cat ?? ia.f1Cat,
          attributeType: 'category',
          importance: 'standard',
        });
      }
      if (ia._routing == 'Orders') {
        badges.attributes.push({
          attributeName: '',
          attributeValue: ia.f1Cat,
          attributeType: 'category',
          importance: 'standard',
        });

        ia.f2Cat && badges.tags.push({
          tagText: ia.f2Cat,
          importance: 'standard',
          iconName: 'information-circle',
          color: ia.f3Cat as GuidedV2TagStatus,
        });
      }

      if (ia._routing == 'Trainings') {
        badges.attributes.push({
          attributeName: '',
          attributeValue: ia.f1Cat,
          attributeType: 'category',
          importance: 'standard',
        });

        ia.documentReference && badges.attributes.push({
          attributeName: '',
          attributeValue: ia.documentReference,
          attributeType: 'category',
          importance: 'standard',
        });

        ia.f2Cat && badges.tags.push({
          tagText: ia.f2Cat,
          importance: 'standard',
          iconName: '',
          color: ia.f3Cat as GuidedV2TagStatus,
        });
      }

      if (ia._routing == 'Quotes') {
        badges.attributes.push({
          attributeName: '',
          attributeValue: ia.f1Cat,
          attributeType: 'category',
          importance: 'standard',
        });

        ia.f2Cat && badges.tags.push({
          tagText: ia.f2Cat,
          importance: 'standard',
          iconName: 'information-circle',
          color: ia.f3Cat as GuidedV2TagStatus,
        });

        if (ia.f1Symbol) {
          badges.attributes.push({
            attributeName: 'ID: ',
            attributeValue: ia.f1Symbol,
            attributeType: 'category',
            importance: 'standard',
          });
        }
      }
      if (ia._routing == 'Company' && ia?.f2Cat == 'Video') {
        badges.tags.push({
          tagText: 'Video Content',
          importance: 'standard',
          color: 'info',
        });
      }

      if (ia._routing == 'Solutions') {
        badges.attributes.push({
          attributeName: '',
          attributeValue: ia.f3Cat ?? ia.f2Cat ?? ia.f1Cat,
          attributeType: 'category',
          importance: 'standard',
        });
      }
      if (ia._routing == 'Services') {
        badges.attributes.push({
          attributeName: '',
          attributeValue: ia.f3Cat ?? ia.f2Cat ?? ia.f1Cat,
          attributeType: 'category',
          importance: 'standard',
        });
      }

      if (ia._routing == 'B2cproducts' && !ia.id) {
        badges.attributes.push({
          attributeName: '',
          attributeValue: ia.f3Cat ?? ia.f2Cat,
          attributeType: 'category',
          importance: 'standard',
        });
      }

      if (ia._routing == 'B2cproducts' && ia.id) {
        badges.attributes.push({
          attributeName: 'Part number',
          attributeValue: ia.id,
          attributeType: 'category',
          importance: 'standard',
        });
      }
      if (ia._routing == 'B2cdocuments') {
        quickLinkOpenUrl = ia?.onlinePreviewUrl;
        quickLinkAttachment = ia.file?.[0];

        badges.attributes.push({
          attributeName: '',
          attributeValue: ia.f3Cat ?? ia.f2Cat,
          attributeType: 'category',
          importance: 'standard',
        });
      }

      if (ia._routing == 'B2cvideotutorials') {
        badges.attributes.push({
          attributeName: '',
          attributeValue: ia.f3Cat ?? ia.f2Cat,
          attributeType: 'category',
          importance: 'standard',
        });
      }

      if (ia._routing == 'Products') {

        if(ia.commStatus) {
          const statusTags = getStatusTag(ia.commStatus);
          statusTags.map( tag => {
              badges.tags.push({
                tagText: tag.text,
                importance: 'standard',
                iconName: 'information-circle',
                color: tag.status,
                type: tag.type
              });
          });

        }
        // if we have more than 1 attribute for Product, don't render category label
        const result: GuidedItemV2<GuidedProductQuickLink> = {
          searcher: searcher,
          category: groupId,
          template: getTemplate(),
          score: item._score,
          details: {
            quickLinkTitle: quickLinkTitle,
            quickLinkOpenUrl: quickLinkOpenUrl,
            quickLinkBadges: badges,
            quickLinkDescription: ia?.description ?? undefined,
            _id: item._id,
            id: ia.id,
            country: 'country' in context && context.country?.toLowerCase(),
            language: 'language' in context && context.language?.toLowerCase(),
            quickLinkImage: ia?.imageUrl ?? undefined,
            isCTO: ia?.isCTO ?? undefined,

          }
        };

        return result;
      }

      //*/
      const result: GuidedItemV2<GuidedGenericQuickLink> = {
        searcher: searcher,
        category: groupId,
        template: getTemplate(),
        score: item._score,
        details: {
          _id: item._id,
          quickLinkOpenUrl: quickLinkOpenUrl,
          quickLinkTitle: quickLinkTitle,
          quickLinkDescription: ia?.description ?? undefined,
          quickLinkBadges: badges,
          quickLinkImage: ia?.imageUrl ?? undefined,
          quickLinkAttachment: quickLinkAttachment ?? undefined,
        }
      };
      return result;

    } catch (e) {
      err('Failed to map Cose 2 Guided item:', {groupDetails, item});
      return null;
    }

};


export const mapCoseToGuidedV2 = async (
  details: GuidedApiCallParams,
  next
) => {

    // info('Enter mapCoseToGuidedV2 middleware', {details});
    try {
      const data = await next(details) as SuggestionsResultV2ResponseDto<
      AmbigousSuggestion,
      CoseContextLocale
    >;

      const returnItems: GuidedItemsV2 = data.groups.map(group => {
          return group.documents.map(item => mapCoseItem({
            searcher: data.searcher,
            groupId: group.id,
            context: details.guidedRequest.context,
          }, item
          ));
      }).flat().filter(item => item != null);

      // info(`Converted ${returnItems.length} items`, {returnItems});

      // info('Returning convertedresults', {returnItems});
      return returnItems;

    } catch (e) {
      err('Exception happened', {e, details});
      info('Returning empty result list, due to exception');
      return [];
    }
};
